import { createRouteMethodologyFilter, defineDashboard, isKEur, resolveProduct } from '../../../_shared/dashboard-axes';
import {
  NameAscDashboardDataTransformer,
  SortByTimeColumnDataTransformer,
  SortByYearDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import { createIdNameTransformers } from '../../../../dashboard-config/granularities/granularity-profile';
import { createKpiFilters } from '../../../../dashboard-config/kpis/kpi-filters';
import {
  KpiColumnDataTransformer,
  KpiExportDataTransformer,
  KpiLabelDataTransformer,
} from '../../../../dashboard-config/kpis/kpi-data-transformers';
import { createTimeFilters, createPeriodLabelTransformers, createPeriodYearTransformers, resolveTimeFilter, timeFilterDetail } from '../../../_shared/time-filters';
import { CreateDashboardDefinitionBase } from '../../../_shared/dashboard-definition.model';
import { ComparisonIndustriesMetricProfile } from './dashboard-comparison-industries-metric.profile';
import {
  DashboardComparisonIndustriesDefinition,
} from './dashboard-comparison-industries.definition';
import {
  DashboardComparisonIndustriesShell,
  dashboardComparisonIndustriesShellProfiles,
} from './dashboard-comparison-industries-shell.profile';

const VALUE_KEY = 'value';

export type CreateDashboardComparisonIndustriesDefinitionInput =
  CreateDashboardDefinitionBase<
    DashboardComparisonIndustriesShell,
    ComparisonIndustriesMetricProfile
  >;

export function createDashboardComparisonIndustriesDefinition(
  input: CreateDashboardComparisonIndustriesDefinitionInput,
): DashboardComparisonIndustriesDefinition {
  const { axes, shell, metric } = resolveProduct(input, dashboardComparisonIndustriesShellProfiles);
  const granularityId = axes.granularity.id;
  const valueKey = VALUE_KEY;
  const time = resolveTimeFilter(axes, 'range');
  const { group } = axes.granularity;

  return defineDashboard<DashboardComparisonIndustriesDefinition>(axes, shell, {
    heading: shell.createHeading({ metric }),

    filters: (deps) => [
      ...createTimeFilters(deps, time),
      ...shell.buildGranularityFilters(deps),
      ...createKpiFilters(axes.metric, granularityId),
      createRouteMethodologyFilter(deps),
    ],

    preFilterTransformers: (deps) => [
      ...createPeriodLabelTransformers(deps, axes),
      new UndefinedStateToSumGermanyDashboardDataTransformer(),
    ],

    postFilterTransformers: (deps) => [
      new KpiColumnDataTransformer({ measure: 'value', resultColumn: valueKey, context: axes.kpiContext }),
      ...createIdNameTransformers(deps, granularityId),
      new NameAscDashboardDataTransformer('sub_market_name'),
      new KpiLabelDataTransformer(),
      ...createPeriodYearTransformers(axes),
      time.kind === 'period' ? new SortByTimeColumnDataTransformer('quarter') : new SortByYearDataTransformer(),
    ],

    exportTransformers: (_deps) => [new KpiExportDataTransformer({ kpis: axes.metric.kpis(), percentageMeasures: [] })],

    view: {
      valueKey,
      seriesKey: metric.colKey,
      groupKey: group.valueKey,
      groupLabelKey: group.nameKey,
      multiSelectName: group.filterName,
      showPercentageChange: shell.viewDefaults.showPercentageChange,
      mainBarLabelHeight: metric.mainBarLabelHeight,
      periodFilterName: time.kind === 'period' ? timeFilterDetail(time).series.filterName : undefined,
      formatAsKEur: isKEur(axes),
      // Absent rather than false for count metrics, as the profiles declared it.
      popup: { ...metric.popup, ...(isKEur(axes) ? { kEur: true } : {}) },
    },
  });
}
